import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Copy, Eye, EyeOff, ExternalLink } from "lucide-react";

// preview only: the public photo copies don't exist yet, so each photo is a plain grey box (the copied code is untouched)
const BOX = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==";
const greyBoxes = (html) => html.replace(/<img src="[^"]*"/g, `<img src="${BOX}"`)
  .replace("</head>", "<style>img{aspect-ratio:4/3;width:100%;max-width:800px}</style></head>");
const btn = "inline-flex items-center gap-1.5 h-11 px-3 rounded-lg border-2 border-slate-300 bg-white text-sm font-bold text-slate-800 hover:bg-slate-50";

// THE PAGE AND ITS STRUCTURED DATA (S2.4) — a PRIVATE preview behind the login, made from the public listing only.
// Nothing is published in Stage 2: to check it with Google, copy the page code and paste it into the Rich Results Test.
export function PagePreview({ v, sd }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get(`/sales/draft/${v.kind}/${v.id}/page`).then((r) => setPage(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open, v.kind, v.id, v.doc_rev]);
  const copy = async () => {
    const r = page || (await api.get(`/sales/draft/${v.kind}/${v.id}/page`)).data;
    try { await navigator.clipboard.writeText(r.html); toast.success("Page code copied — paste it into Google's Rich Results Test, on its Code tab."); }
    catch { toast.error("This browser didn't allow copying. Open the page preview and copy from there."); }
  };
  return (
    <div data-testid="sales-draft-page" className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button type="button" data-testid="sales-draft-page-toggle" onClick={() => setOpen(!open)} className={btn}>
          {open ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{open ? "Hide the page" : "Show the page"}</button>
        <button type="button" data-testid="sales-draft-page-copy" onClick={copy} className={btn}><Copy className="w-4 h-4" />Copy the page code</button>
        <a data-testid="sales-draft-page-rich-results" href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer" className={btn}>
          <ExternalLink className="w-4 h-4" />Google's Rich Results Test</a>
      </div>
      {open && (page
        ? <iframe data-testid="sales-draft-page-frame" title="The listing's page — private preview" sandbox="" referrerPolicy="no-referrer" srcDoc={greyBoxes(page.html)}
            className="w-full h-[520px] rounded-lg border border-slate-300 bg-white" />
        : <p className="text-sm text-slate-500">Reading the page…</p>)}
      {open && page && <p data-testid="sales-draft-page-photo-note" className="text-xs text-slate-500">The photos show as grey boxes here: their public copies are made when it is published (S2.5). Each box carries the photo's words as its alt text.</p>}
      <details data-testid="sales-draft-structured" className="rounded-lg border border-slate-200 p-2">
        <summary className="cursor-pointer text-sm font-semibold text-slate-800">Structured data ({sd.length} {sd.length === 1 ? "block" : "blocks"}: {sd.map((x) => [].concat(x["@type"]).join(" + ")).join(", ")})</summary>
        <pre data-testid="sales-draft-structured-json" className="mt-2 max-h-80 overflow-auto text-[11px] leading-snug bg-slate-50 p-2 rounded">{JSON.stringify(sd, null, 2)}</pre>
      </details>
    </div>
  );
}
